/**
 * CreateFailureModeModal Component
 *
 * Standalone modal for creating new failure modes from anywhere in the app.
 * Used by CommandPalette and CauseEffectPage.
 *
 * Features:
 * - Real-time RPN calculation (Severity × Occurrence × Detection)
 * - Form validation (name required, S/O/D values 1-10)
 * - 5M+E categorization dropdown
 * - Success/error notifications via react-hot-toast
 * - Optimistic updates with React Query
 */

import React, { useMemo, useRef, useState } from 'react'
import {
  useCreateCauseEffect,
  type CreateCauseEffectInput,
} from '../../hooks/useCauseEffect'
import { useTerminology } from '../../hooks/useTerminology'
import { useFocusTrap } from '../interlock/useFocusTrap'
import './CreateFailureModeModal.css'

interface CreateFailureModeModalProps {
  isOpen: boolean
  onClose: () => void
}

export function CreateFailureModeModal({ isOpen, onClose }: CreateFailureModeModalProps): React.JSX.Element | null {
  const { t } = useTerminology()
  // Accessible modal semantics + focus management, same shared primitive as
  // Interlock's RiskIntakeModal: focus moves into the dialog on open, Tab is
  // trapped, Escape closes, focus returns to the trigger on close.
  const panelRef = useFocusTrap<HTMLDivElement>(isOpen, onClose)
  // SoW-255-001: Removed mitigationStrategy and contributingFactors
  // These should be linked as proper entities after creation, not orphaned text
  const [formData, setFormData] = useState<CreateCauseEffectInput>({
    type: 'FAILURE_MODE',
    category: 'MAN',
    description: '',
    severity: 5,
    occurrence: 5,
    detection: 5,
  })

  const createMutation = useCreateCauseEffect()
  // Synchronous double-submit guard — mirrors RiskIntakeModal's
  // `submittingRef` (RiskIntakeModal.tsx). `createMutation.isPending` only
  // flips on the NEXT render, so a fast double-Enter/double-click can fire
  // `handleSubmit` twice before React re-renders with the disabled button,
  // producing two 201s. This ref is set synchronously at the top of
  // `handleSubmit`, so the second call in the same tick is a guaranteed
  // no-op regardless of render timing.
  const submittingRef = useRef(false)

  // Real-time RPN calculation: Severity × Occurrence × Detection
  const calculatedRpn = useMemo(
    () => (formData.severity || 1) * (formData.occurrence || 1) * (formData.detection || 1),
    [formData.severity, formData.occurrence, formData.detection]
  )

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target

    // Handle numeric fields (S/O/D). Don't early-return on an empty/invalid
    // intermediate value (e.g. mid-clear via select-all+Backspace) — that
    // used to leave state (and the controlled input) stuck at the old value,
    // silently dropping every subsequent keystroke. Coerce instead, same as
    // the shared FormField component's number handling; the native
    // min/max/required attributes on the input plus submit-time validation
    // still block an out-of-range value from being saved.
    if (name === 'severity' || name === 'occurrence' || name === 'detection') {
      const numValue = parseInt(value, 10)
      setFormData((prev) => ({ ...prev, [name]: isNaN(numValue) ? 0 : numValue }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()

    // Validate required fields
    if (!formData.description.trim()) {
      return // Form validation will show error
    }

    if (submittingRef.current) return
    submittingRef.current = true

    // Submit to backend API
    createMutation.mutate(formData, {
      onSuccess: () => {
        // Close modal and reset form on success
        submittingRef.current = false
        onClose()
        setFormData({
          type: 'FAILURE_MODE',
          category: 'MAN',
          description: '',
          severity: 5,
          occurrence: 5,
          detection: 5,
        })
      },
      onError: () => {
        submittingRef.current = false
      },
      // Toast is shown by the mutation hook; the ref reset above is what
      // actually re-arms the guard for a retry.
    })
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleCancel = (): void => {
    onClose()
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div
        ref={panelRef}
        className="modal-content create-failure-mode-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-failure-mode-title"
      >
        <h2 id="create-failure-mode-title">Create {t.failureMode}</h2>

        <form onSubmit={handleSubmit}>
          {/* Row 1: Type and Category */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Type</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
              >
                <option value="FAILURE_MODE">{t.failureMode}</option>
                <option value="ROOT_CAUSE">{t.rootCause}</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="category">
                Category (5M+E)
                <span className="field-hint">Required</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="MAN">Man (Human Error)</option>
                <option value="MACHINE">Machine (Equipment)</option>
                <option value="MATERIAL">Material (Defects)</option>
                <option value="METHOD">Method (Process)</option>
                <option value="MEASUREMENT">Measurement (Calibration)</option>
                <option value="ENVIRONMENT">Environment (External)</option>
              </select>
            </div>
          </div>

          {/* Row 2: Description */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="description">
                Description
                <span className="field-hint">Required</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={3}
                placeholder={`Detailed description of the ${t.failureMode.toLowerCase()} or ${t.rootCause.toLowerCase()}`}
                maxLength={500}
              />
            </div>
          </div>

          {/* Row 3: FMEA Ratings (Severity, Occurrence, Detection) */}
          <div className="form-row fmea-ratings-row">
            <div className="form-group">
              <label htmlFor="severity">
                Severity (S)
                <span className="field-hint">1-10</span>
              </label>
              <input
                type="number"
                id="severity"
                name="severity"
                min="1"
                max="10"
                value={formData.severity}
                onChange={handleChange}
                required
              />
              <small className="field-description">
                1 = Minor, 10 = Catastrophic
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="occurrence">
                Occurrence (O)
                <span className="field-hint">1-10</span>
              </label>
              <input
                type="number"
                id="occurrence"
                name="occurrence"
                min="1"
                max="10"
                value={formData.occurrence}
                onChange={handleChange}
                required
              />
              <small className="field-description">
                1 = Rare, 10 = Frequent
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="detection">
                Detection (D)
                <span className="field-hint">1-10</span>
              </label>
              <input
                type="number"
                id="detection"
                name="detection"
                min="1"
                max="10"
                value={formData.detection}
                onChange={handleChange}
                required
              />
              <small className="field-description">
                1 = Easy to detect, 10 = Cannot detect
              </small>
            </div>

            <div className="form-group rpn-display">
              <label>{t.rpn} (Calculated)</label>
              <div className="rpn-value-display" aria-label="Calculated RPN">
                <span className="rpn-number">{calculatedRpn}</span>
                <span className="rpn-formula">S × O × D</span>
              </div>
              <small className="field-description">
                {calculatedRpn <= 40 && 'Low Risk'}
                {calculatedRpn > 40 && calculatedRpn <= 120 && 'Medium Risk'}
                {calculatedRpn > 120 && calculatedRpn <= 200 && 'High Risk'}
                {calculatedRpn > 200 && 'Critical Risk'}
              </small>
            </div>
          </div>

          {/* SoW-255-001: Removed Mitigation Strategy and Contributing Factors rows
              These should be linked as proper entities via the detail view panel,
              not entered as orphaned free text. Mitigations are phase-specific
              and need proper tracking. Root causes should be separate CauseEffect entities. */}

          {/* Actions */}
          <div className="modal-actions">
            <button
              type="button"
              onClick={handleCancel}
              disabled={createMutation.isPending}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !formData.description.trim()}
              className="btn btn-primary"
            >
              {createMutation.isPending ? 'Creating...' : `Create ${t.failureMode}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
